import {
  initUpgradeFareToPremium,
  initUpgradeFareToPremiumSuccess,
  loadSeatMapsSuccess,
  seatLoadSeatMaps,
  updatePassengerSeats,
  updatePassengerSeatsSuccess,
} from '@actions/seat.action';
import { Injectable } from '@angular/core';
import { BaseError, ErrorType } from '@models/app.models';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { SeatService } from '@services/api-clients/seat.service';
import { TripService } from '@services/api-clients/trip.service';
import { FALLBACK_CURRENCY } from '@constants/app.constants';
import { DATE_FORMAT_YEAR_MONTH_DATE } from '@constants/date-format.constants';
import {
  TripSellRequest,
  N0SellKey,
} from '@models/requests/trip-sell-request.model';
import {
  selectAvailabilitySelectedTripsState,
  selectSearchInputState,
  selectAvailabilitySourceOrganization,
} from '@selectors/availability.selector';
import { selectBooking } from '@selectors/booking.selector';
import { selectSelectedCurrencyCode } from '@selectors/culture.selector';
import { DateTime } from 'luxon';
import { PassengerTypeCode } from '@models/booking.models';
import { bookingGetBooking } from '@actions/booking.action';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { onError } from '../actions/app.action';
import { SsrService } from '@services/api-clients/ssr.service';

@Injectable()
export class SeatEffect {
  loadSeatMaps$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(seatLoadSeatMaps),
      mergeMap(() => {
        return this.seatService
          .getSeatMaps()
          .pipe(
            switchMap(({ data: seatMaps }) =>
              of(loadSeatMapsSuccess({ seatMaps })),
            ),
          );
      }),
    );
  });

  updatePassengerSeats$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updatePassengerSeats),
      mergeMap((action) => {
        return forkJoin([
          this.seatService.updatePassengerSeats(action.updates),
          this.ssrService.ssrUpdate(action.ssrs),
        ]).pipe(
          switchMap(() => {
            return of(
              updatePassengerSeatsSuccess({
                redirectToPayment: action.redirectToPayment,
              }),
            );
          }),
          catchError(() => {
            return of(
              onError({
                error: new BaseError(ErrorType.PassengerSeatsUpdateFailure),
              }),
            );
          }),
        );
      }),
    );
  });

  initUpgradeFareToPremium$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(initUpgradeFareToPremium),
      mergeMap(() => {
        return of(
          initUpgradeFareToPremiumSuccess(),
        );
      }),
    );
  });

  initUpgradeFareToPremiumSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(initUpgradeFareToPremiumSuccess),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySelectedTripsState),
        this.store.select(selectSearchInputState),
        this.store.select(selectBooking),
        this.store.select(selectSelectedCurrencyCode),
        this.store.select(selectAvailabilitySourceOrganization),
      ]),
      mergeMap(
        ([
          ,
          selectedTrips,
          searchInput,
          booking,
          currencyCode,
          sourceOrganization,
        ]) => {
          const request: TripSellRequest = {
            firstFlightDate: searchInput?.criteria[0]?.beginDate,
            keys:
              selectedTrips?.filter<N0SellKey>(
                (t): t is N0SellKey => t !== null,
              ) ?? [],
            passengers: {
              types: (searchInput?.passengers ?? []).filter(
                (p) => p.type !== PassengerTypeCode.INF,
              ),
            },
            infantCount: (searchInput?.passengers ?? []).find(
              (p) => p.type === PassengerTypeCode.INF,
            )?.count,
            infantBirthDates: (searchInput?.passengers ?? [])
              .find((p) => p.type === PassengerTypeCode.INF)
              ?.dobs?.map((d) =>
                DateTime.fromISO(d).toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
              ),
            childCount: (searchInput?.passengers ?? []).find(
              (p) => p.type === PassengerTypeCode.CHD,
            )?.count,
            childBirthDates: (searchInput?.passengers ?? [])
              .find((p) => p.type === PassengerTypeCode.CHD)
              ?.dobs?.map((d) =>
                DateTime.fromISO(d).toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
              ),
            currencyCode:
              booking?.currencyCode ?? currencyCode ?? FALLBACK_CURRENCY,
            promotionCode: searchInput?.promotionCode,
            sourceOrganization,
          };
          return this.tripService
            .sellTrip(request, false)
            .pipe(map(() => bookingGetBooking({})));
        },
      ),
    );
  });

  constructor(
    private actions$: Actions,
    private seatService: SeatService,
    private tripService: TripService,
    private store: Store,
    private ssrService: SsrService,
  ) {}
}
